import { Box, Divider, Grid, IconButton, List, ListItem, ListItemText, Paper, Snackbar, Stack, Switch, Tooltip, Typography } from '@mui/material'
import { useHistory } from 'react-router-dom'
import { DCDashboardContainer } from '../../assets/theme/theme';
import { useCaseTrackingContext } from '../../contexts/CaseTrackingContext';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { KeyboardArrowLeft } from '@mui/icons-material';
import Moment from 'react-moment';
import theme from '../../assets/theme/theme';
import CloseIcon from "@mui/icons-material/Close";
import parse from 'html-react-parser';

const HighlightAnchors = ({ html }) => {
  const options = {
    replace: (domNode) => {
      if (
        domNode.name === 'a' &&
        domNode.attribs &&
        domNode.attribs.target === '_blank'
      ) {
        return (
          <a
            href={domNode.attribs.href}
            target="_blank"
            style={{ color: theme.palette.primary.main, textDecoration: 'underline' }}
            rel="noopener noreferrer"
          >
            {domNode.children[0].data}
          </a>
        );
      }
    },
  };

  return <div>{parse(html, options)}</div>;
};


const CaseDetails = () => {
  // get url param
  const history = useHistory();
  const receiptNumber = history.location.pathname.split('/').pop()

  const { getParticularCase, caseDetails, setCaseDetails, deleteCaseTracking, refreshCase, updateCaseTracking } = useCaseTrackingContext();
  const [openSnackBarDelete, setOpenSnackBarDelete] = useState(false);
  const [openSnackBarRefresh, setOpenSnackBarRefresh] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleSnackbarCloseDelete = useCallback(() => {
    setOpenSnackBarDelete(false);
  }, [setOpenSnackBarDelete]);

  const handleSnackbarCloseRefresh = useCallback(() => {
    setOpenSnackBarRefresh(false);
  }, [setOpenSnackBarRefresh]);

  const actionDelete = (
    <Fragment>
      <IconButton
        size="small"
        aria-label="close"
        color="inherit"
        onClick={handleSnackbarCloseDelete}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Fragment>
  );

  const actionRefresh = (
    <Fragment>
      <IconButton
        size="small"
        aria-label="close"
        color="inherit"
        onClick={handleSnackbarCloseRefresh}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Fragment>
  );

  useEffect(() => {
    if (receiptNumber) {
      getParticularCase(receiptNumber);
    }
  }, [receiptNumber, getParticularCase]);

  return (
    <>
      {caseDetails ? (
        <DCDashboardContainer>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
            <Box display="flex">
              <IconButton onClick={() => history.push('/case-tracking')}>
                <KeyboardArrowLeft />
              </IconButton>
              <Typography variant="h4" gutterBottom mt={1}>
                Case: {caseDetails.receiptNumber}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" ml={2}>
                ( Refreshed at <Moment fromNow>{new Date(caseDetails.refreshAt)}</Moment> )
              </Typography>
            </Box>
            <Box>
              Tracking:
              <Switch
                onClick={() => {
                  if (caseDetails.tracking) {
                    setCaseDetails(prev => ({
                      ...prev,
                      tracking: false
                    }));
                    updateCaseTracking(receiptNumber!, false);
                  } else {
                    setCaseDetails(prev => ({
                      ...prev,
                      tracking: true
                    }));
                    updateCaseTracking(receiptNumber!, true);
                  }
                }}
                checked={caseDetails.tracking}
                color="primary"
                inputProps={{ 'aria-label': 'Enable tracking' }}
              />
              {caseDetails.refreshAt && new Date(caseDetails.refreshAt).getTime() > Date.now() - 10 * 60 * 1000 ? (
                <Tooltip title="You can only refresh the case details once every 10 minutes.">
                  <LoadingButton
                    type='button'
                    variant="contained"
                    sx={{
                      mr: 1,
                      backgroundColor: theme.palette.grey[400],
                      color: theme.palette.common.white,
                      "&:hover": { backgroundColor: theme.palette.grey[400], cursor: 'not-allowed' }
                    }}
                  >
                    Refresh
                  </LoadingButton>
                </Tooltip>
              ) : (
                <LoadingButton
                  type='button'
                  variant="contained"
                  onClick={async () => {
                    setRefreshing(true);
                    await refreshCase(receiptNumber!);
                    setOpenSnackBarRefresh(true);
                    setRefreshing(false);
                  }}
                  loading={refreshing}
                  loadingPosition="center"
                  sx={{ mr: 1 }}
                  disabled={caseDetails.refreshAt && new Date(caseDetails.refreshAt).getTime() > Date.now() - 10 * 60 * 1000} // disable if refreshed within last 10 minutes
                >
                  Refresh
                </LoadingButton>
              )}
              <LoadingButton
                type='button'
                variant="contained"
                color="error"
                onClick={async () => {
                  setDeleting(true);
                  await deleteCaseTracking(receiptNumber!);
                  setOpenSnackBarDelete(true);
                  setDeleting(false);
                  history.push('/case-tracking')
                }}
                loading={deleting}
                loadingPosition="center"
              >
                Delete
              </LoadingButton>
            </Box>
          </Box>

          <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
            <Stack direction="row" mb={2} gap={0.5}>
                <Typography variant="subtitle2">Current Case Status - </Typography>
                <Typography>{caseDetails.current_case_status_text_en}</Typography>
            </Stack>

            <HighlightAnchors html={caseDetails.current_case_status_desc_en} />

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2">Form Type</Typography>
                <Typography>{caseDetails.formType}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2">Receipt Number</Typography>
                <Typography>{caseDetails.receiptNumber}</Typography>
              </Grid>
              {!caseDetails.receiptNumber.startsWith("IOE") && (
                <>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">Submitted Date</Typography>
                    <Typography>{caseDetails.submittedDate}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">Last Updated</Typography>
                    <Typography>{caseDetails.modifiedDate}</Typography>
                  </Grid>
                </>
              )}
            </Grid>
          </Paper>

          <Typography variant="h6" gutterBottom>
            Case History
          </Typography>

          <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
            <List>
              {caseDetails.hist_case_status ? (
                <>
                  {caseDetails.hist_case_status.map((entry, idx) => (
                    <ListItem key={idx} alignItems="flex-start" disableGutters>
                      <ListItemText
                        primary={
                          <Typography variant="body1" sx={{ whiteSpace: 'normal' }}>
                            {entry.date} — {entry.completed_text_en}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'normal' }}>
                            {entry.completed_text_es}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No case history available.
                </Typography>
              )}
            </List>
          </Paper>
        </DCDashboardContainer>
      ) : (
        <DCDashboardContainer>
          <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
            <Typography variant="h5" color="text.secondary">
              Fetching case details...
            </Typography>
          </Box>
        </DCDashboardContainer>
      )}
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        sx={{
          ".MuiSnackbarContent-root": {
            background: theme.palette.success.main,
          },
        }}
        open={openSnackBarDelete}
        autoHideDuration={6000}
        message="Case deleted successfully."
        onClose={handleSnackbarCloseDelete}
        action={actionDelete}
      />
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        sx={{
          ".MuiSnackbarContent-root": {
            background: theme.palette.success.main,
          },
        }}
        open={openSnackBarRefresh}
        autoHideDuration={6000}
        message="Case refreshed successfully."
        onClose={handleSnackbarCloseRefresh}
        action={actionRefresh}
      />
    </>
  )
}

export default CaseDetails